import React, { ChangeEvent, FocusEvent, useEffect, useState } from 'react'
import { TextField, TextFieldProps } from '@mui/material'
import { formatNumber, formatPercentage } from '../../packages/utils/numberFormatters'
import { Control, useController, UseControllerProps, useFormContext } from 'react-hook-form'

export type PlainNumericFieldProps = Omit<TextFieldProps, 'onChange'> & {
    decimals?: number
    minDecimals?: number | null
    maxDecimals?: number | null
    onChange?: (value: string) => void
    percentage?: boolean
    validChars?: string | null
    value: string
}

export const PlainNumericField = ({
    decimals,
    minDecimals = 0,
    maxDecimals = 0,
    onBlur,
    onChange,
    percentage = false,
    validChars,
    value,
    ...textFieldProps
}: PlainNumericFieldProps) => {
    const [isFocused, setIsFocused] = useState(false)
    const locale = 'sv-SE' // TODO get from i18n
    const formatProps = { locale, decimals, minDecimals, maxDecimals }

    if (!minDecimals && !decimals) minDecimals = 0

    const handleFocus = () => {
        setIsFocused(true)
    }

    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
        setIsFocused(false)

        if (onBlur) onBlur(event)
    }

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const v = event.target.value?.trim() || ''
        validChars = validChars || '0123456789.,'
        const cleanValue = v
            .split('')
            .filter((c) => validChars?.includes(c))
            .join('')
            .replace(',', '.')

        if (onChange) onChange(cleanValue)
    }

    const renderDisplayValue = (value: string | number) => {
        if (value === null || value === undefined || value === '') return '' // Handle null properly
        return percentage ? formatPercentage(value, formatProps) : formatNumber(value, formatProps)
    }

    const renderValue = (value: string | number) => {
        if (value === null || value === undefined || value === '') return '' // Handle null properly
        return value
    }

    return (
        <TextField
            {...textFieldProps}
            value={isFocused ? renderValue(value) : renderDisplayValue(value)}
            onFocus={handleFocus}
            slotProps={{
                ...textFieldProps.slotProps,
                input: {
                    onBlur: handleBlur,
                    onChange: handleChange,
                },
            }}
        />
    )
}

type NumerFieldProps = Omit<PlainNumericFieldProps, 'value'> & {
    name: string
    isDecimal?: boolean
    control: Control
    onChangeValue?: ((val: string) => void) | null
    validChars?: string | null
    validation?: UseControllerProps['rules']
}

const NumericField = ({
    isDecimal,
    label,
    name,
    control,
    onChangeValue = null,
    validation = {}, // Accept validation prop
    validChars = null,
    ...plainNumericFieldProps
}: NumerFieldProps) => {
    const {
        field,
        fieldState: { error },
    } = useController({
        name,
        control,
        rules: validation,
    })
    const { disabled } = plainNumericFieldProps

    const formMethods = useFormContext()
    const watchedValue = formMethods?.watch(name) // Dynamically watch the field's value

    useEffect(() => {
        if (!disabled || formMethods.getValues()[name] === null) return

        formMethods.setValue(name, null)
    }, [disabled, formMethods.getValues, formMethods.setValue, name])

    const handleBlur = () => {
        field.onBlur()

        void formMethods.trigger(name) // Explicitly trigger validation
    }

    const handleChange = (value: string) => {
        if (onChangeValue) onChangeValue(value)

        if (value === '') {
            field.onChange(null)
        } else if (!isDecimal) {
            field.onChange(+value)
        } else {
            if (value.slice(-1) === '.') {
                field.onChange(value)
            } else {
                field.onChange(parseFloat(value))
            }
        }

        void formMethods.trigger(name) // Explicitly trigger validation
    }

    return (
        <PlainNumericField
            {...plainNumericFieldProps}
            label={label}
            value={watchedValue}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!error}
            helperText={error ? error.message : ''}
            validChars={validChars}
        />
    )
}

type RegisteredNumericFieldProps = Omit<NumerFieldProps, 'control'>

const RegisteredNumericField = (props: RegisteredNumericFieldProps) => {
    const formMethods = useFormContext()
    const { control } = formMethods
    const forwardProps = { control, ...props }
    return <NumericField isDecimal {...forwardProps} />
}

/**
 * A RegisteredNumericField that accepts decimal numbers. Default minDecimals is 2 and maxDecimals is 5.
 */
const RegisteredDecimalField = (props: RegisteredNumericFieldProps) => {
    const forwardProps = { minDecimals: 2, maxDecimals: 5, ...props }
    return <RegisteredNumericField isDecimal {...forwardProps} />
}

/**
 * A RegisteredNumericField that only accepts integers, ie only chars 0-9 can be entered.
 */
const RegisteredIntegerField = ({
    allowNegative,
    ...props
}: { allowNegative?: boolean } & RegisteredNumericFieldProps) => {
    let validChars = '0123456789'

    if (allowNegative) {
        validChars = validChars + '-'
    }

    const forwardProps = { decimals: 0, validChars, ...props }
    return <RegisteredNumericField {...forwardProps} />
}

export { RegisteredNumericField, RegisteredDecimalField, RegisteredIntegerField }
